import apiServices from "../apiServices";
import { validateOrThrowApiResponse } from "../response-validator";

type AnagramPayloadItem = {
    id?: number | string;
    no: number;
    expected_time?: number;
    word: string;
    swap_word: string[];
    hint?: string | null;
    sound_word?: string | null;
    image_word?: string | null;
    sound_swap_word?: string | null;
    image_swap_word?: string | null;
    sound_hint?: string | null;
    image_hint?: string | null;
};

type AnagramSkillPayload = {
    skill_listening_speaking?: boolean;
    skill_lang_nature?: boolean;
    skill_reading?: boolean;
    skill_writing?: boolean;
};

type AnagramAudioPayload = {
	audio_question?: boolean;
	audio_choice?: boolean;
	audio_assistant_voice?: boolean;
	audio_background?: boolean;
};

type CreateAnagramPayload = AnagramSkillPayload & AnagramAudioPayload & {
    gameId: number | string;
    anagrams: AnagramPayloadItem[];
};

type GetAnagramByIdPayload = {
    gameId: number | string;
};

type UpdateAnagramPayload = AnagramSkillPayload & AnagramAudioPayload & {
    anagramId: number | string;
    word?: string;
    swap_word?: string[];
    no?: number;
    expected_time?: number;
    hint?: string | null;
    sound_word?: string | null;
    image_word?: string | null;
    sound_swap_word?: string | null;
    image_swap_word?: string | null;
    sound_hint?: string | null;
    image_hint?: string | null;
};

type DeleteAnagramPayload = {
    anagramId: number | string;
};

const ANAGRAM_MEDIA_FIELDS = [
    "sound_word",
    "sound_swap_word",
    "image_word",
    "image_swap_word",
    "hint",
    "sound_hint",
    "image_hint",
] as const;

type AnagramMediaField = (typeof ANAGRAM_MEDIA_FIELDS)[number];

type HardDeleteMediaFieldPayload = {
    anagramId: number | string;
    field: AnagramMediaField;
};


const anagramAPI = {

    createAnagram(payload: CreateAnagramPayload) {
        const requestBody: {
            anagrams: AnagramPayloadItem[];
            skill_listening_speaking?: boolean;
            skill_lang_nature?: boolean;
            skill_reading?: boolean;
            skill_writing?: boolean;
            audio_question?: boolean;
            audio_choice?: boolean;
            audio_assistant_voice?: boolean;
            audio_background?: boolean;
        } = {
            anagrams: payload.anagrams,
        };

        if (payload.skill_listening_speaking !== undefined) {
            requestBody.skill_listening_speaking = payload.skill_listening_speaking;
        }
        if (payload.skill_lang_nature !== undefined) {
            requestBody.skill_lang_nature = payload.skill_lang_nature;
        }
        if (payload.skill_reading !== undefined) {
            requestBody.skill_reading = payload.skill_reading;
        }
        if (payload.skill_writing !== undefined) {
            requestBody.skill_writing = payload.skill_writing;
        }
        if (payload.audio_question !== undefined) {
            requestBody.audio_question = payload.audio_question;
        }
        if (payload.audio_choice !== undefined) {
            requestBody.audio_choice = payload.audio_choice;
        }
        if (payload.audio_assistant_voice !== undefined) {
            requestBody.audio_assistant_voice = payload.audio_assistant_voice;
        }
        if (payload.audio_background !== undefined) {
            requestBody.audio_background = payload.audio_background;
        }

        return apiServices
            .post(`/api/game/${payload.gameId}/anagrams`, requestBody, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error createAnagram:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    getAnagramById(payload: GetAnagramByIdPayload) {
        return apiServices
            .get(`/api/game/${payload.gameId}/anagrams`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error getAnagramById:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    getAllAnagrams() {
        return apiServices
            .get(`/api/anagrams`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error getAllAnagrams:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    updateAnagram(payload: UpdateAnagramPayload) {
        const requestBody: {
            word?: string;
            swap_word?: string[];
            no?: number;
            expected_time?: number;
            hint?: string | null;
            sound_word?: string | null;
            image_word?: string | null;
            sound_swap_word?: string | null;
            image_swap_word?: string | null;
            sound_hint?: string | null;
            image_hint?: string | null;
            skill_listening_speaking?: boolean;
            skill_lang_nature?: boolean;
            skill_reading?: boolean;
            skill_writing?: boolean;
            audio_question?: boolean;
            audio_choice?: boolean;
            audio_assistant_voice?: boolean;
            audio_background?: boolean;
        } = {};

        if (payload.word !== undefined) requestBody.word = payload.word;
        if (payload.swap_word !== undefined) requestBody.swap_word = payload.swap_word;
        if (payload.no !== undefined) requestBody.no = payload.no;
        if (payload.expected_time !== undefined) requestBody.expected_time = payload.expected_time;
        if (payload.hint !== undefined) requestBody.hint = payload.hint;
        if (payload.sound_word !== undefined) requestBody.sound_word = payload.sound_word;
        if (payload.image_word !== undefined) requestBody.image_word = payload.image_word;
        if (payload.sound_swap_word !== undefined) requestBody.sound_swap_word = payload.sound_swap_word;
        if (payload.image_swap_word !== undefined) requestBody.image_swap_word = payload.image_swap_word;
        if (payload.sound_hint !== undefined) requestBody.sound_hint = payload.sound_hint;
        if (payload.image_hint !== undefined) requestBody.image_hint = payload.image_hint;
        if (payload.skill_listening_speaking !== undefined) {
            requestBody.skill_listening_speaking = payload.skill_listening_speaking;
        }
        if (payload.skill_lang_nature !== undefined) {
            requestBody.skill_lang_nature = payload.skill_lang_nature;
        }
        if (payload.skill_reading !== undefined) {
            requestBody.skill_reading = payload.skill_reading;
        }
        if (payload.skill_writing !== undefined) {
            requestBody.skill_writing = payload.skill_writing;
        }
        if (payload.audio_question !== undefined) {
            requestBody.audio_question = payload.audio_question;
        }
        if (payload.audio_choice !== undefined) {
            requestBody.audio_choice = payload.audio_choice;
        }
        if (payload.audio_assistant_voice !== undefined) {
            requestBody.audio_assistant_voice = payload.audio_assistant_voice;
        }
        if (payload.audio_background !== undefined) {
            requestBody.audio_background = payload.audio_background;
        }

        return apiServices
            .put(`/api/anagrams/${payload.anagramId}`, requestBody, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error updateAnagram:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    hardDeleteAnagram(payload: DeleteAnagramPayload) {
        return apiServices
            .delete(`/api/anagrams/${payload.anagramId}`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error hardDeleteAnagram:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    softDeleteAnagram(payload: DeleteAnagramPayload) {
        return apiServices
            .patch(`/api/anagrams/${payload.anagramId}/soft-delete`, {}, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error softDeleteAnagram:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    hardDeleteMediaFileAndHint(payload: HardDeleteMediaFieldPayload) {
        const allowed = new Set<string>(ANAGRAM_MEDIA_FIELDS as readonly string[]);
        if (!allowed.has(String(payload.field))) {
            return Promise.resolve({
                status: "failed",
                errMessage: `invalid field: ${String(payload.field)}`,
                error: "invalid_field",
            });
        }

        return apiServices
            .patch(`/api/anagrams/${payload.anagramId}/hard-delete-field`, { field: payload.field }, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error hardDeleteMediaFileAndHint:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    }

}

export default anagramAPI;